import React, {useCallback, useEffect, useRef, useState} from 'react'
import PropTypes, {arrayOf} from "prop-types"
import {useTranslation} from "react-i18next";
import {TWENTY_FIVE_MEGABYTES} from "../../utils/FileUtils";
import {errorMessage, getValue, isInvalid, onChange} from "./Component";
import {useNavigate, useParams} from "react-router-dom";
import Modal from "./Modal";
import Button from "./Button";

const FILE_SIZE_LIMIT = TWENTY_FIVE_MEGABYTES;

const FileUpload = (props) => {
  const {t} = useTranslation();

  const [dragCounter, setDragCounter] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [onZone, setOnZone] = useState(false);
  const [files, setFiles] = useState([]);
  const [value, setValue] = useState(null);
  const [deleted, setDeleted] = useState(false);
  const [showDropZone, setShowDropZone] = useState(false);
  const [fileInDropZone, setFileInDropZone] = useState(false);
  const navigate = useNavigate();
  const [showPopup, setShowPopup] = useState(false);
  const [cancelClicked, setCancelClicked] = useState(false);

  function getClassName(props) {
    return (showDropZone ? "file is-info field" : "field")
      + (onZone && dragging ? " is-dragging" : "")
      + (isInvalid(props) ? "file is-info field is-invalid" : "")
      + (files.length > 1 ? " is-loading" : "")
      + (props.disabled ? " is-disabled" : "")
  }

  useEffect(() => {
    if (deleted === true)
      return;

    if (!getValue(props)) {
      setShowDropZone(true)
      return;
    }

    setShowDropZone(false)
    setFileInDropZone(true)
    let val = getValue(props);
    setFiles([val])

  }, [getValue(props)]);

  const dropZone = React.createRef();

  useEffect(() => {

    const div = dropZone.current;

    if (cancelClicked) {
      setShowPopup(false)
    }

    if (showPopup) {
      setCancelClicked(false)
    }

    div.addEventListener('dragenter', handleDragIn);
    div.addEventListener('dragleave', handleDragOut);
    div.addEventListener('dragover', handleDrag);
    if (!fileInDropZone) {
      div.addEventListener('drop', handleDrop);
    }
    window.addEventListener("dragover", handleBlockDrop);
    window.addEventListener("drop", handleBlockDrop);


    return () => {
      div.removeEventListener('dragenter', handleDragIn);
      div.removeEventListener('dragleave', handleDragOut);
      div.removeEventListener('dragover', handleDrag);
      if (!fileInDropZone) {
        div.removeEventListener('drop', handleDrop);
      }
      window.removeEventListener("dragover", handleBlockDrop);
      window.removeEventListener("drop", handleBlockDrop);
    }
  }, [fileInDropZone, showPopup, cancelClicked])


  useEffect(() => {
    if(getValue(props.formField) === '' && files[0] == null) {
      return;
    }
    props.formField.setValue(files[0]);

  }, [files[0], value]);

  /**
   * Prevents browser to open file when file is dropped outside of drop zone
   * @param event Drop and DragOver
   * @private
   */
  function handleBlockDrop(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  function handleDrag(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  function handleDragIn(event) {
    event.preventDefault();
    event.stopPropagation();
    setDragCounter(dragCounter + 1);
    setDragging(true);
    if (event.dataTransfer.items && event.dataTransfer.items.length > 0) {
      setOnZone(true);
    }
  }

  function handleDragOut(event) {
    event.preventDefault();
    event.stopPropagation();
    setDragCounter(dragCounter - 1)
    if (dragCounter < 1) {
      setDragging(false);
      setOnZone(false);
    }
  }

  /**
   * When files are dropped into zone
   * @param event DragEvent
   * @private
   */
  function handleDrop(event) {
    if (!fileInDropZone || files.length < 1) {
      event.preventDefault();
      event.stopPropagation();
      setDragging(false);
      setOnZone(false);
      handleFileEventForDrop(event.dataTransfer.files);
      event.dataTransfer.clearData();
      setDragCounter(0);
    }
  }

  function handleFileEventForDrop(files = []) {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setFileInDropZone(true)
      setShowDropZone(false)
      if (file.size > FILE_SIZE_LIMIT) {
        props.onFileError && props.onFileError("FILE_SIZE_ERROR");
      } else {
        const fileReader = new FileReader();
        fileReader.onload = handleBase64File.bind(undefined, file);
        fileReader.readAsDataURL(file);
      }
    }
  }

  function handleFileEvent(files = []) {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size > FILE_SIZE_LIMIT) {
        props.onFileError && props.onFileError("FILE_SIZE_ERROR");
      } else {
        const fileReader = new FileReader();
        fileReader.onload = handleBase64File.bind(undefined, file);
        fileReader.readAsDataURL(file);
      }
    }
  }

  function handleBase64File(file, fileEvent) {
    //data URL has some meta database prefix, we need to remove it to have pure base64 content
    setFiles([...files, {
      name: file.name,
      type: file.type,
      size: file.size,
      content: fileEvent.target.result.replace(/data.*base64,/, "")
    }]);
  }

  /**
   * When files are selected using dialog
   * @param event SyntheticEvent
   * @private
   */
  function onFileChange(event) {

    handleFileEvent(event.target.files);
    event.target.value = "";
    setShowDropZone(false)
    setFileInDropZone(true)
  }

  const deleteByValue = (value) => {
    setDeleted(true)
    setFiles([]);
    setShowDropZone(true)
    setShowPopup(false)
    setFileInDropZone(false)
  }

  const handleCancel = (value) => {
    setCancelClicked(true);
    setShowPopup(false)
  }

  const handleDeleteButton = (value) => {
    setShowPopup(true)
    if (navigator.userAgent.indexOf("Firefox") !== -1) {
      setCancelClicked(false)
    }
  }

  const images = files.map(file => {
    if (files.length > 1) {
      files.splice(1);
      setFiles(files)
    }
    if (file !== '') {
      return <div key={file.name}>
        <div>
          <Button
            className="delete-image-button icon fz fz-trash-alt show-delete-on-small-screens"
            color="primary"
            isOutlined={true}
            onClick={() => {
              handleDeleteButton()
            }}
          />
          <img className="uploaded-image"
               src={`data:image/;base64,${file.content}`}/>
        </div>
        <Modal title={t('Delete image')}
               content={"This image will be removed"}
               showPopup={showPopup}
               label={"Delete"}
               confirmButtonColor={"primary"}
               onClose={() =>
                 handleCancel()
               }
               onDelete={() => deleteByValue()}>
        </Modal>
      </div>
    }
  });


  return (
    <div>
      <label
        className={getClassName(props)}
        ref={dropZone}>
        {showDropZone
          ?
          <input
            id="uploadHidden"
            className="is-hidden"
            type="file"
            accept=".png,.jpeg"
            onChange={onFileChange}
            disabled={props.disabled}
            name="filename"/> : null}
        {showDropZone
          ?
          <span className="file-cta">
          <span className="file-icon"><i className="fz fz-cloud-upload solid"/></span>
          <span className="file-label">{t("Choose or drag a file")}</span>
        </span> : null
        }
        {images}
      </label>
    </div>
  )
}

FileUpload.propTypes = {
  isInvalid: PropTypes.bool,
  onFileDrop: PropTypes.func,
  onFileError: PropTypes.func,
  disabled: PropTypes.bool,
};

export default FileUpload
